import { Search as SearchIcon } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import React, { FC, useCallback, useEffect, useState } from 'react';
import GridData from 'src/components/GridData/GridData';
import { SaveAndStartJoinCampaignButton } from 'src/modules/campaigns/components/SaveAndStartJoinCampaignButton';
import { StatusChip, BhiDialog, TextInput } from 'src/UIKit';
import { AddContactsToGroupDialogController } from '../../controllers';

interface AddContactsToGroupDialogProps extends AddContactsToGroupDialogController {
  onSuccess?: () => void;
}

export const AddContactsToGroupDialog: FC<AddContactsToGroupDialogProps> = props => {
  const { isOpen, close, groupId, groupName, onSuccess } = props;

  const dialogTitle = groupName ? `Add Contacts for ${groupName}` : 'Add Contacts';

  return (
    <BhiDialog isOpen={isOpen} onClose={close} title={dialogTitle} paperSx={{ minWidth: '700px' }}>
      {!!groupId && <Table {...props} onSuccess={onSuccess} />}
    </BhiDialog>
  );
};

const Table: FC<AddContactsToGroupDialogProps> = props => {
  const { groupId, loading, filter, list, changeSearch, changePagination, submit, onSuccess } =
    props;

  const [newContactIds, setNewContactIds] = useState<string[]>([]);
  const [addedContactIds, setAddedContactIds] = useState<string[]>([]);

  const save = async () => {
    const added = await submit(newContactIds, onSuccess ? async () => onSuccess() : undefined);
    return added ? newContactIds : undefined;
  };

  const selectNewContactIds = useCallback(
    (selectedIds: string[]) => {
      const newIds = selectedIds.filter(id => !addedContactIds.includes(id));
      setNewContactIds(newIds);
    },
    [addedContactIds],
  );

  useEffect(() => {
    if (groupId && list) {
      setAddedContactIds(currentIds => {
        const newIds = list.items.filter(c => c.groups.some(g => g.id === groupId)).map(c => c.id);
        return [...new Set([...currentIds, ...newIds])];
      });
    }
  }, [groupId, list]);

  return (
    <Box p={2.5}>
      <TextInput
        placeholder="Search"
        value={filter.query}
        onChange={(_, v) => changeSearch(v)}
        endIcon={<SearchIcon />}
        margin="none"
        sx={{ mb: 2.25 }}
      />

      <Box height={500}>
        <GridData
          isLoading={loading.get}
          rows={list?.items}
          checkboxSelection={{
            rowSelectionModel: [...newContactIds, ...addedContactIds],
            onRowSelectionModelChange: selectNewContactIds,
            hideSelectAllCheckbox: true,
            hideFooterSelectedRowCount: true,
            isRowSelectable: params => params.row.groups.every(g => g.id !== groupId),
          }}
          columns={[
            {
              field: 'name',
              headerName: 'Name',
              renderCell: r => {
                return (
                  <>
                    {r.row.firstName} {r.row.lastName}
                  </>
                );
              },
              minWidth: 160,
              editable: false,
              sortable: false,
              flex: 1,
            },
            {
              field: 'phoneNumber',
              headerName: 'Phone Number',
              minWidth: 120,
              editable: false,
              sortable: false,
              flex: 1,
            },
            {
              field: 'validStatus',
              headerName: 'Status',
              renderCell: r => {
                if (r.row.validStatus == 'invalid') {
                  return (
                    <StatusChip label="Invalid" />
                  );
                }

                if (r.row.optOutDate) {
                  return (
                    <StatusChip label="Opted Out" tone="brandDark" />
                  );
                }

                return (
                  <StatusChip label="Active" tone="brandLight" />
                );
              },
              width: 75,
              editable: false,
              sortable: false,
            },
            {
              field: 'email',
              headerName: 'Email',
              minWidth: 140,
              editable: false,
              sortable: false,
              flex: 1,
            },
          ]}
          pagination={{
            page: filter.page,
            pageSize: filter.limit,
            totalCount: list?.count,
            onChange: changePagination,
          }}
        />
      </Box>

      <Grid
        container
        spacing={2}
        sx={{ alignItems: 'center', justifyContent: 'space-between', mt: 1 }}
      >
        <Grid item>
          <Typography variant="body2" fontStyle="italic">
            {newContactIds.length} rows selected
          </Typography>
        </Grid>

        <Grid item>
          <SaveAndStartJoinCampaignButton
            label="Save"
            disabled={!newContactIds.length || loading.add}
            groupIds={groupId ? [groupId] : []}
            onSave={save}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
